import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ApolloService } from '../../services/apollo.service';
import { ToastService } from '../../services/toast/toast.service';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import * as fromCatalog from '../notification-settings-list/graphql';
import { ANNOUNCEMENT_STATUS_VALUES, announcementStatusLabelKey } from './announcement-labels';
import * as fromGraphQl from './graphql';

/** Server-Default. Kein totalCount — deshalb „Mehr laden" statt Seitenzahlen. */
const PAGE_SIZE = 20;

export const ANNOUNCEMENT_LIST_PATH = '/settings/announcements';
export const ANNOUNCEMENT_FORM_PATH = `${ANNOUNCEMENT_LIST_PATH}/new`;

interface FilterOption {
  label: string;
  value: string;
}

/**
 * Was der Hinweisstreifen braucht: die Zahlen des Durchlaufs plus Id und Titel, um ihn benennen und
 * wieder aufnehmen zu koennen.
 */
interface IncompleteRetraction extends fromGraphQl.RetractionProgress {
  id: string;
  title: string;
}

/**
 * Announcement-Verwaltung (#738).
 *
 * Plattform-Broadcasts durch Nexato: verfassen, terminieren, den Versand verfolgen. Fachlich ein
 * reines Nexato-Feature — kein Mandant verschickt Announcements.
 *
 * Die Cursor-Mechanik liegt bewusst NICHT im ReactiveContainer: der ersetzt seine Daten bei jeder
 * Emission, „Mehr laden" muss aber anhaengen. Vorlage ist notification-delivery-list (#737).
 * nx-simple-paginator scheidet aus — sein totalElements ist input.required, und die API liefert
 * bewusst kein totalCount.
 */
@Component({
  selector: 'app-announcement-list',
  templateUrl: './announcement-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnouncementListComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Einziger Filter. Leer bedeutet „alle" und wird als fehlender Schluessel gesendet, nicht als null. */
  readonly status = signal<string | null>(null);

  readonly items = signal<fromGraphQl.Announcement[]>([]);
  private readonly nextCursor = signal<string | null>(null);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly error = signal(false);
  readonly cancelling = signal(false);
  readonly retracting = signal(false);
  readonly hasMore = computed(() => this.nextCursor() !== null);

  /**
   * Ein unvollstaendig zurueckgezogener Versand — der Grund, warum ein Rueckzug nach einem Klick
   * nicht als erledigt gelten darf.
   *
   * Es gibt KEINEN Hintergrundjob, der einen abgebrochenen Durchlauf nachholt; ein erneuter Aufruf
   * derselben Mutation IST die Wiederaufnahme. Client-Dok §8.3 verlangt dafuer einen sichtbaren
   * Hinweis „in die Oberflaeche, nicht nur ein gruenes Haekchen" — deshalb ein STEHENDER Streifen
   * und kein Toast: der waere weg, bevor jemand „Erneut versuchen" druecken kann.
   *
   * Die Zahlen kommen aus der MUTATIONSANTWORT, nicht aus der neu geladenen Liste:
   * retractionProgress ist ueberall sonst null.
   */
  readonly incompleteRetraction = signal<IncompleteRetraction | null>(null);

  /** typeKey -> server-uebersetztes Label. Fehlt der Katalog, zeigt die Zeile den typeKey. */
  private readonly typeLabels = signal<Map<string, string>>(new Map());

  /**
   * Werte sind exakt die Enum-Namen — AnnouncementStatus.valueOf(status) ist case-sensitiv, ein
   * abweichender Wert erzeugt eine ValidationProblem.
   */
  readonly statusOptions: FilterOption[] = ANNOUNCEMENT_STATUS_VALUES.map((value) => ({
    label: announcementStatusLabelKey(value),
    value
  }));

  ngOnInit(): void {
    this.loadTypeLabels();
    this.loadFirstPage();
  }

  typeLabel(typeKey: string): string | null {
    return this.typeLabels().get(typeKey) ?? null;
  }

  onStatusChange(status: string | null): void {
    this.status.set(status ?? null);
    this.loadFirstPage();
  }

  loadFirstPage(): void {
    this.loading.set(true);
    this.error.set(false);

    this.queryPage(undefined).subscribe({
      next: (result) => {
        const page = result?.data?.announcements;
        this.items.set(page?.items ?? []);
        this.nextCursor.set(page?.nextCursor ?? null);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  /**
   * Das Ende der Liste sagt ausschliesslich nextCursor === null. Nie aus einer kuerzeren Antwort als
   * angefragt darauf schliessen, und nirgends eine Gesamtzahl behaupten — items().length ist keine.
   */
  loadMore(): void {
    const cursor = this.nextCursor();
    if (!cursor || this.loadingMore()) {
      return;
    }
    this.loadingMore.set(true);

    this.queryPage(cursor).subscribe({
      next: (result) => {
        const page = result?.data?.announcements;
        this.items.update((items) => [...items, ...(page?.items ?? [])]);
        this.nextCursor.set(page?.nextCursor ?? null);
        this.loadingMore.set(false);
      },
      error: () => {
        // Bestehende Zeilen bleiben stehen, der Knopf bleibt aktiv fuer einen erneuten Versuch.
        this.loadingMore.set(false);
      }
    });
  }

  onCompose(): void {
    this.router.navigate([ANNOUNCEMENT_FORM_PATH]);
  }

  /**
   * Duplizieren ist rein clientseitig: eine NEUE Ankuendigung mit dem Inhalt einer alten. Seit es
   * updateAnnouncement gibt, ist das eine eigene Absicht und nicht mehr der Ersatz fuers Aendern.
   *
   * Der vollstaendige Datensatz liegt in der Liste bereits vor; ein erneuter Query-Aufruf dafuer
   * waere reine Latenz — und es gibt ohnehin keine Einzelabfrage im Schema.
   */
  onDuplicate(announcement: fromGraphQl.Announcement): void {
    this.router.navigate([ANNOUNCEMENT_FORM_PATH], { state: { announcement } });
  }

  /**
   * Aendern und Korrigieren oeffnen dasselbe Formular in einem anderen Modus. Beide geben den
   * Datensatz im Router-State mit, weil das Schema KEINE Einzelabfrage hat (`announcements(status,
   * cursor, limit)` ist die einzige Query) — die Bearbeiten-Route kann sich nach einem Reload
   * nichts nachladen und leitet dann hierher zurueck.
   *
   * Beim Aendern reist auch `version` mit: sie ist die optimistische Sperre von
   * updateAnnouncement. Ist der Listenstand veraltet, lehnt der Server ab und das Formular schickt
   * den Admin mit einer Meldung wieder hierher.
   */
  onEdit(announcement: fromGraphQl.Announcement): void {
    this.router.navigate([ANNOUNCEMENT_LIST_PATH, announcement.id, 'edit'], { state: { announcement } });
  }

  onCorrect(announcement: fromGraphQl.Announcement): void {
    this.router.navigate([ANNOUNCEMENT_LIST_PATH, announcement.id, 'correct'], { state: { announcement } });
  }

  /**
   * Abbrechen geht nur solange SCHEDULED. Das ist ein Wettlauf: die Liste kann SCHEDULED zeigen,
   * waehrend der Fan-out laengst laeuft — dann wirft der Server eine ValidationProblem, weil
   * cancelIfScheduled keine Zeile trifft.
   */
  onCancel(announcement: fromGraphQl.Announcement): void {
    if (this.cancelling()) {
      return;
    }
    const message = this.translate.instant('core.announcement.cancel.confirmMessage', {
      title: announcement.content?.['de']?.title ?? ''
    });

    const dialogRef = this.confirmDialogService.open({
      header: 'core.announcement.cancel.confirmHeader',
      // Fertig gerendert uebergeben: ConfirmDialogService kann keine Parameter, keine Umbrueche und
      // kein HTML — er rendert stur {{message | translate}}.
      message,
      severity: ConfirmDialogSeverity.WARN,
      confirmLabel: 'core.announcement.cancel.confirmLabel'
    });

    dialogRef.onClose
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmDialogEventType) => {
        if (result === ConfirmDialogEventType.CONFIRM) {
          this.executeCancel(announcement.id);
        }
      });
  }

  /**
   * Zurueckziehen einer laufenden oder abgeschlossenen Ankuendigung.
   *
   * Die Rueckfrage traegt den Einbahnstrassen-Hinweis: `correctAnnouncement` verlangt RUNNING oder
   * DONE, nach dem Rueckzug ist eine Richtigstellung fuer immer weg (Client-Dok §8.3 — „Die
   * Oberflaeche sagt das VOR dem Klick, nicht erst ueber die Fehlermeldung danach"). Umgekehrt geht
   * es weiter: eine richtiggestellte Ankuendigung laesst sich jederzeit noch zurueckziehen.
   */
  onRetract(announcement: fromGraphQl.Announcement): void {
    if (this.retracting()) {
      return;
    }
    const title = announcement.content?.['de']?.title ?? '';
    const message = this.translate.instant('core.announcement.retract.confirmMessage', { title });

    const dialogRef = this.confirmDialogService.open({
      header: 'core.announcement.retract.confirmHeader',
      // Fertig gerendert uebergeben: ConfirmDialogService kann keine Parameter, keine Umbrueche und
      // kein HTML — er rendert stur {{message | translate}}.
      message,
      severity: ConfirmDialogSeverity.WARN,
      confirmLabel: 'core.announcement.retract.confirmLabel'
    });

    dialogRef.onClose
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmDialogEventType) => {
        if (result === ConfirmDialogEventType.CONFIRM) {
          this.executeRetract(announcement.id, title);
        }
      });
  }

  /**
   * Wiederaufnahme eines unvollstaendigen Durchlaufs — dieselbe Mutation, bewusst OHNE erneute
   * Rueckfrage: die Absicht steht, der Dialog waere eine Bremse ohne Nutzen. Die Einbahnstrasse ist
   * mit dem ersten Aufruf ohnehin schon genommen.
   */
  onRetryRetraction(): void {
    const pending = this.incompleteRetraction();
    if (pending) {
      this.executeRetract(pending.id, pending.title);
    }
  }

  private executeRetract(id: string, title: string): void {
    this.retracting.set(true);

    this.apolloService
      .mutateObservable<fromGraphQl.RetractAnnouncementResponse>(fromGraphQl.RETRACT_ANNOUNCEMENT_MUTATION, { id })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const progress = result?.data?.retractAnnouncement?.retractionProgress;
          this.retracting.set(false);
          // Unvollstaendig: Hinweis stehen lassen und KEINEN Erfolg melden. Vollstaendig (oder
          // ohne Fortschrittsangabe, dann gibt es nichts zu behaupten): Hinweis weg, Erfolg.
          if (progress && progress.retractedTenants < progress.totalTenants) {
            this.incompleteRetraction.set({ id, title, ...progress });
          } else {
            this.incompleteRetraction.set(null);
            this.toastService.addToastWithMessage(
              this.translate.instant('core.announcement.retract.successToastMessage')
            );
          }
          this.loadFirstPage();
        },
        error: () => {
          // Wahrscheinlichster Fall ist ein Statuswechsel zwischenzeitlich — also neu laden, damit
          // die Zeile zeigt, was jetzt gilt.
          this.toastService.addErrorToast('core.announcement.retract.errorMessage');
          this.retracting.set(false);
          this.loadFirstPage();
        }
      });
  }

  private executeCancel(id: string): void {
    this.cancelling.set(true);

    // mutateObservable mit { next, error }: mutate() und mutateAndExecute() subscriben ohne
    // error-Callback und verschlucken den Fehler (apollo.service.ts:11-35).
    this.apolloService
      .mutateObservable<fromGraphQl.CancelAnnouncementResponse>(fromGraphQl.CANCEL_ANNOUNCEMENT_MUTATION, { id })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.addToastWithMessage(this.translate.instant('core.announcement.cancel.successToastMessage'));
          this.cancelling.set(false);
          this.loadFirstPage();
        },
        error: () => {
          // Der wahrscheinlichste Fehler ist genau der Wettlauf. Statt des englischen Server-Textes
          // eine verstaendliche Meldung — und neu laden, denn genau dann ist der Listenstand veraltet.
          this.toastService.addErrorToast('core.announcement.cancel.alreadyStartedMessage');
          this.cancelling.set(false);
          this.loadFirstPage();
        }
      });
  }

  /**
   * `status` wird bei leerem Filter GAR NICHT mitgesendet. Ein null-Wert waere serverseitig zwar
   * zulaessig, aber der Filter ist optional und ein fehlender Schluessel ist die eindeutige Form von
   * „alle".
   *
   * fetchPolicy network-only ist hier Pflicht: der Default von apollo.query ist cache-first (die
   * network-only-Voreinstellung in graphql.module.ts gilt nur fuer watchQuery). Nach einem Abbruch
   * zeigte die Liste sonst weiter SCHEDULED aus dem Cache.
   */
  private queryPage(cursor: string | undefined) {
    const variables: { status?: string; cursor?: string; limit: number } = { cursor, limit: PAGE_SIZE };
    const status = this.status();
    if (status) {
      variables.status = status;
    }

    return this.apolloService
      .query<fromGraphQl.AnnouncementPageResponse>(fromGraphQl.ANNOUNCEMENTS_QUERY, variables, {
        fetchPolicy: 'network-only'
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef));
  }

  /**
   * Typ-Labels aus dem Katalog (#735-Query, wiederverwendet). Der Announcement-Datensatz traegt nur
   * den typeKey; das Label kommt fertig uebersetzt vom Server und wird NIE durch die translate-Pipe
   * geschickt. Faellt der Katalog aus, bleibt der typeKey in der Zeile stehen.
   */
  private loadTypeLabels(): void {
    this.apolloService
      .query<fromCatalog.CatalogQueryResponse>(fromCatalog.NOTIFICATION_CATALOG_QUERY, {
        locale: this.translate.currentLang ?? this.translate.getDefaultLang() ?? 'de'
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const map = new Map<string, string>();
          for (const type of result?.data?.catalog ?? []) {
            map.set(type.typeKey, type.label);
          }
          this.typeLabels.set(map);
        },
        error: () => this.typeLabels.set(new Map())
      });
  }
}
